import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import styled, { css, keyframes } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { formatPrice } from 'Utils/tools';
import BasketBarEcoResponsibleModal from './BasketBarEcoResponsibleModal';
import { IconHeart, IconTrash } from 'Ui/Icons';
import {
  getBasket_basket_basketSales_basketProducts,
  getBasket_basket_basketSales_sale,
} from '../HeaderBasket/__generated__/getBasket';
import { theme } from 'Ui';
import AuthStore from 'Store/AuthStore';
import { allowedTypes } from 'Utils/memberTypes';
import { gtmUpdateItemQttyItem, gtmDeleteItemCard } from 'Libs/GA4/common';

export const DELETE_PRODUCT = gql`
  mutation BasketBarDeleteProduct($basketProductId: ID!) {
    deleteBasketProduct(basketProductId: $basketProductId) {
      id
      totalPrice
      productCount
      updatedAt
      remainingTime
      basketSales {
        basketProducts {
          basketProductId
          productId
          price
          totalPrice
          totalEcoTax
          totalPublicPrice
          productCount
          quantityMax
          size
          product {
            id
            groupId
            title
            publicPrice
            sellPrice
            appliedPrice
            ecoParticipation
            discountPercentage
            brandDatas {
              name
            }
            sizeDatas {
              productId
              size
              stock
              maxQuantity
              sellPrice
              publicPrice
            }
            imagesDatas {
              urlListPage
            }
          }
        }
        sale {
          id
          name
          minimumDeliveryDate
          maximumDeliveryDate
          productDeliveryDate
          promoMulti
          promoCategory
          saleArea
        }
      }
    }
  }
`;

export const DELETE_AND_ADD_TO_WISHLIST = gql`
  mutation BasketBarDeleteAndAddToWishlist($basketProductId: ID!) {
    addBasketProductToWishlist(basketProductId: $basketProductId) {
      id
      totalPrice
      productCount
      updatedAt
      remainingTime
      basketSales {
        basketProducts {
          basketProductId
          productId
          price
          totalPrice
          totalEcoTax
          totalPublicPrice
          productCount
          quantityMax
          size
          product {
            id
            groupId
            title
            publicPrice
            sellPrice
            appliedPrice
            ecoParticipation
            discountPercentage
            brandDatas {
              name
            }
            sizeDatas {
              productId
              size
              stock
              maxQuantity
              sellPrice
              publicPrice
            }
            imagesDatas {
              urlListPage
            }
          }
        }
        sale {
          id
          name
          minimumDeliveryDate
          maximumDeliveryDate
          productDeliveryDate
          promoMulti
          promoCategory
          saleArea
        }
      }
    }
  }
`;

export const UPDATE_SIZE = gql`
  mutation BasketBarUpdateSize(
    $basketProductId: ID!
    $productId: ID!
    $quantity: Int!
  ) {
    updateBasketProductSize(
      basketProductId: $basketProductId
      productId: $productId
      quantity: $quantity
    ) {
      id
      totalPrice
      productCount
      updatedAt
      remainingTime
      basketSales {
        basketProducts {
          basketProductId
          productId
          price
          totalPrice
          totalEcoTax
          totalPublicPrice
          productCount
          quantityMax
          size
          product {
            id
            groupId
            title
            publicPrice
            sellPrice
            appliedPrice
            ecoParticipation
            discountPercentage
            brandDatas {
              name
            }
            sizeDatas {
              productId
              size
              stock
              maxQuantity
              sellPrice
              publicPrice
            }
            imagesDatas {
              urlListPage
            }
          }
        }
        sale {
          id
          name
          minimumDeliveryDate
          maximumDeliveryDate
          productDeliveryDate
          promoMulti
          promoCategory
          saleArea
        }
      }
    }
  }
`;

export const RE_ADD_PRODUCT = gql`
  mutation BasketBarReAddProduct(
    $productId: ID!
    $quantity: Int!
    $saleId: ID!
  ) {
    addProductToBasket(
      productId: $productId
      quantity: $quantity
      saleId: $saleId
    ) {
      id
      productCount
      totalPrice
      totalSaving
      totalPricePlusShipping
      basketPromoCode {
        code
        value
      }
      basketSales {
        basketProducts {
          basketProductId
          productId
          price
          totalPrice
          totalEcoTax
          totalPublicPrice
          productCount
          quantityMax
          size
          product {
            id
            groupId
            title
            publicPrice
            sellPrice
            appliedPrice
            ecoParticipation
            discountPercentage
            brandDatas {
              name
            }
            sizeDatas {
              productId
              size
              stock
              maxQuantity
              sellPrice
              publicPrice
            }
            imagesDatas {
              urlListPage
            }
          }
        }
        sale {
          id
          name
          minimumDeliveryDate
          maximumDeliveryDate
          productDeliveryDate
          promoMulti
          promoCategory
          saleArea
        }
      }
    }
  }
`;

export const UPDATE_QUANTITY = gql`
  mutation BasketBarUpdateQuantity($basketProductId: ID!, $quantity: Int!) {
    setBasketProductQuantity(
      basketProductId: $basketProductId
      quantity: $quantity
    ) {
      id
      totalPrice
      productCount
      updatedAt
      remainingTime
      basketSales {
        basketProducts {
          basketProductId
          productId
          price
          totalPrice
          totalEcoTax
          totalPublicPrice
          productCount
          quantityMax
          size
          product {
            id
            groupId
            title
            publicPrice
            sellPrice
            appliedPrice
            ecoParticipation
            discountPercentage
            brandDatas {
              name
            }
            sizeDatas {
              productId
              size
              stock
              maxQuantity
              sellPrice
              publicPrice
            }
            imagesDatas {
              urlListPage
            }
          }
        }
        sale {
          id
          name
          minimumDeliveryDate
          maximumDeliveryDate
          productDeliveryDate
          promoMulti
          promoCategory
          saleArea
        }
      }
    }
  }
`;

const slideLeft = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  margin: 1.6rem 0;
  width: 100%;
`;

const ImageWrapper = styled.div`
  width: 70px;
  margin: 0 1.8rem 0 0;
  flex-shrink: 0;
  img {
    width: 70px;
    display: block;
    object-fit: cover;
  }
`;

const TitleSizeBlock = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  .title {
    max-width: 14rem;
    font-family: var(--font-primary);
    font-size: 1.4rem;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #000;
    line-height: normal;
  }

  .size {
    font-family: var(--font-primary);
    font-size: 1.4rem;
    font-weight: 400;
    margin: 8px 0;
    color: #000;
  }
`;

const ProductLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  align-items: flex-end;
`;

const BzcPrice = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 700;
  text-align: right;
`;

const BzcPublicPrice = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: line-through;
  color: #aaa;
  text-align: right;
`;

const EcoTax = styled.div`
  font-family: var(--font-primary);
  font-size: 1.1rem;
  color: #999;
  text-align: right;
  cursor: pointer;

  span {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const SizeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0px 0;
`;

const SizeLabel = styled.span`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  color: #000;
`;

const ModifyBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 500;
  color: #777;
  text-decoration: underline;
  text-underline-offset: 2px;
  line-height: 1;

  &:hover {
    color: #000;
  }
`;

const SizeSelectorWrapper = styled.div`
  position: relative;
`;

const SizeDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #3a3a3a;
  border-radius: 8px;
  padding: 4px 0;
  min-width: 120px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
`;

const SizeOption = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  color: #fff;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: ${({ $isActive }) => ($isActive ? 700 : 400)};
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const UrgencyText = styled.p`
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 600;
  color: #c0392b;
  /* text-decoration: underline; */
  text-underline-offset: 2px;
  margin-bottom: 6px;
`;

const CounterRow = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  width: fit-content;
  margin-top: 4px;
`;

const CounterBtn = styled.button<{ $disabled?: boolean }>`
  background: none;
  border: none;
  width: 2.8rem;
  height: 2.8rem;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $disabled }) => ($disabled ? '#ccc' : '#000')};
  &:hover:not([disabled]) {
    background: #f5f5f5;
  }
`;

const CounterValue = styled.span`
  font-family: var(--font-primary);
  font-size: 1.3rem;
  min-width: 2.4rem;
  text-align: center;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-bottom: 1.6rem;
  gap: 1.2rem;
`;

const ActionWording = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 500;
  color: #777;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    color: #000;
  }
`;

const FeedbackBanner = styled.div`
  display: flex;
  width: 100%;
  height: 11.3rem;
  padding: 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  background: #f7f7f7;
  margin-bottom: 1.6rem;

  svg path,
  svg circle,
  svg rect {
    stroke: #000;
  }
`;

const FeedbackTitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 500;
  color: #000;
`;

const FeedbackSubText = styled.div`
  font-family: var(--font-primary);
  font-size: 1.1rem;
  font-weight: 400;
  color: #2f2f2f;
  text-align: center;
`;

const FeedbackUndo = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 500;
  color: #2f2f2f;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  &:hover {
    /* color: #fff; */
  }
`;

const Separator = styled.div`
  width: 100%;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    display: none;
  }
`;

const imageCropParams = JSON.stringify({
  dimensions: { width: 70, height: 90, crop: true },
});

type Props = {
  productInformation: getBasket_basket_basketSales_basketProducts;
  sale: getBasket_basket_basketSales_sale;
  onBasketUpdate: (data: any) => void;
};

export default ({ productInformation, sale, onBasketUpdate }: Props) => {
  const product = productInformation.product;
  const { t } = useTranslation('shipping', { useSuspense: false });
  const [quantity, setQuantity] = useState(productInformation.productCount);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [wishlistProductInfo, setWishlistProductInfo] = useState({
    brand: '',
    title: '',
  });
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [showEcoModal, setShowEcoModal] = useState(false);
  const sizeSelectorRef = useRef<HTMLDivElement>(null);
  const maxQty = productInformation.quantityMax ?? 10;
  const sessionMemberType = AuthStore.useStoreState(
    (state) => state.sessionMemberType
  );
  const canMoveToWishlist = allowedTypes.includes(sessionMemberType);

  useEffect(() => {
    if (!showSizeSelector) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        sizeSelectorRef.current &&
        !sizeSelectorRef.current.contains(e.target as Node)
      ) {
        setShowSizeSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSizeSelector]);

  const [deleteProduct, { loading: isDeleting }] = useMutation(DELETE_PRODUCT);
  const [deleteAndAddToWishlist, { loading: isMovingToWishlist }] = useMutation(
    DELETE_AND_ADD_TO_WISHLIST
  );
  const [reAddProduct] = useMutation(RE_ADD_PRODUCT);
  const [updateQuantity] = useMutation(UPDATE_QUANTITY);
  const [updateSize] = useMutation(UPDATE_SIZE);

  const basketProductId = productInformation.basketProductId.toString();
  const imageUrl = product?.imagesDatas?.[0]?.urlListPage;
  const linkToProduct = `/ventes/produit/id/${product?.id}/idcat/${sale.id}`;
  const totalPrice = productInformation.totalPrice ?? productInformation.price;
  const totalPublicPrice = productInformation.totalPublicPrice;
  const totalEcoTax = productInformation.totalEcoTax;

  const availableSizes = product?.sizeDatas ?? [];
  const currentSizeData = availableSizes.find(
    (s) => s.size === productInformation.size
  );
  const currentStock = currentSizeData?.stock ?? null;
  const remainingStock = currentStock !== null ? currentStock - quantity : null;
  const hasSizeChoice = availableSizes.filter((s) => s.stock > 0).length > 1;

  const buildTrackingItem = (qty: number) => ({
    item_id: product?.id,
    item_name: product?.title,
    item_brand: product?.brandDatas?.name,
    item_category: sale.saleArea,
    item_list_name: 'basket',
    item_delivery_date: sale.productDeliveryDate,
    price: product?.sellPrice,
    quantity: qty,
    currency: 'EUR',
    discount: `${product?.discountPercentage}%`,
    promotion_id: sale.id,
    promotion_name: sale.name,
  });

  const trackAddToCart = (qty: number) => {
    gtmUpdateItemQttyItem({
      event: 'add_to_cart',
      ecommerce: { items: [buildTrackingItem(qty)] },
    });
  };

  const trackRemoveFromCart = (qty: number) => {
    gtmDeleteItemCard({ items: [buildTrackingItem(qty)] });
  };

  const handleDecrement = async () => {
    if (quantity <= 1 || loadingAction) return;
    const newQty = quantity - 1;
    setQuantity(newQty);
    setLoadingAction('qty');
    try {
      const { data } = await updateQuantity({
        variables: { basketProductId, quantity: newQty },
      });
      if (data?.setBasketProductQuantity) {
        onBasketUpdate(data.setBasketProductQuantity);
        trackRemoveFromCart(newQty);
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleIncrement = async () => {
    if (quantity >= maxQty || loadingAction) return;
    const newQty = quantity + 1;
    setQuantity(newQty);
    setLoadingAction('qty');
    try {
      const { data } = await updateQuantity({
        variables: { basketProductId, quantity: newQty },
      });
      if (data?.setBasketProductQuantity) {
        onBasketUpdate(data.setBasketProductQuantity);
        trackAddToCart(newQty);
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDelete = async () => {
    if (loadingAction) return;
    setLoadingAction('delete');
    try {
      const { data } = await deleteProduct({ variables: { basketProductId } });
      if (data?.deleteBasketProduct) {
        onBasketUpdate(data.deleteBasketProduct);
        trackRemoveFromCart(quantity);
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleMoveToWishlist = async () => {
    if (loadingAction) return;
    setWishlistProductInfo({
      brand: product?.brandDatas?.name ?? '',
      title: product?.title ?? '',
    });
    setLoadingAction('wishlist');
    try {
      const { data } = await deleteAndAddToWishlist({
        variables: { basketProductId },
      });
      if (data?.addBasketProductToWishlist)
        onBasketUpdate(data.addBasketProductToWishlist);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleReAdd = async () => {
    const sizeData = availableSizes.find(
      (s) => s.size === productInformation.size
    );
    if (!sizeData) return;
    try {
      const { data } = await reAddProduct({
        variables: {
          productId: sizeData.productId.toString(),
          quantity,
          saleId: sale.id,
        },
      });
      if (data?.addProductToBasket) onBasketUpdate(data.addProductToBasket);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSizeChange = async (newSize: string) => {
    if (newSize === productInformation.size || loadingAction) return;
    const newSizeData = availableSizes.find((s) => s.size === newSize);
    if (!newSizeData) return;
    setShowSizeSelector(false);
    setLoadingAction('size');
    try {
      const { data } = await updateSize({
        variables: {
          basketProductId,
          productId: newSizeData.productId.toString(),
          quantity,
        },
      });
      if (data?.updateBasketProductSize)
        onBasketUpdate(data.updateBasketProductSize);
    } finally {
      setLoadingAction(null);
    }
  };

  const isLoading = !!loadingAction;

  return (
    <>
      {loadingAction === 'wishlist' && (
        <FeedbackBanner>
          <IconHeart width={24} height={24} />
          <FeedbackTitle>{t('inWishlist')}</FeedbackTitle>
          <FeedbackSubText>
            {wishlistProductInfo.brand} – {wishlistProductInfo.title}
          </FeedbackSubText>
        </FeedbackBanner>
      )}
      {loadingAction === 'delete' && (
        <FeedbackBanner>
          <IconTrash width={40} height={40} />
          <FeedbackTitle>{t('removed')}</FeedbackTitle>
          <FeedbackUndo onClick={handleReAdd}>{t('cancel')}</FeedbackUndo>
        </FeedbackBanner>
      )}
      {!isDeleting && !isMovingToWishlist && (
        <Container style={{ opacity: isLoading ? 0.6 : 1 }}>
          <ProductInfo>
            {imageUrl && (
              <a href={linkToProduct}>
                <ImageWrapper>
                  <img
                    src={`${imageUrl}?params=${imageCropParams}`}
                    alt={product?.title || ''}
                  />
                </ImageWrapper>
              </a>
            )}
            <TitleSizeBlock>
              <ProductLeft>
                <a href={linkToProduct}>
                  <p className="title">{product?.title}</p>
                </a>
                <SizeSelectorWrapper ref={sizeSelectorRef}>
                  <SizeRow>
                    <SizeLabel>
                      {t('sizeLabel')} : {productInformation.size}
                    </SizeLabel>
                    {hasSizeChoice && (
                      <ModifyBtn
                        onClick={() => setShowSizeSelector((v) => !v)}
                        disabled={isLoading}
                      >
                        {t('modify')}
                      </ModifyBtn>
                    )}
                  </SizeRow>
                  {showSizeSelector && (
                    <SizeDropdown>
                      {availableSizes
                        .filter((s) => (s.stock ?? 0) > 0)
                        .map((s) => (
                          <SizeOption
                            key={s.productId}
                            $isActive={s.size === productInformation.size}
                            onClick={() => handleSizeChange(s.size)}
                          >
                            {s.size === productInformation.size && '✓ '}
                            {s.size}
                          </SizeOption>
                        ))}
                    </SizeDropdown>
                  )}
                </SizeSelectorWrapper>
                {remainingStock !== null &&
                  remainingStock >= 0 &&
                  remainingStock < 4 && (
                    <UrgencyText>
                      {t('urgencyStock', { count: remainingStock })}
                    </UrgencyText>
                  )}
                <CounterRow>
                  <CounterBtn
                    onClick={handleDecrement}
                    $disabled={quantity <= 1 || isLoading}
                    disabled={quantity <= 1 || isLoading}
                  >
                    −
                  </CounterBtn>
                  <CounterValue>{quantity}</CounterValue>
                  <CounterBtn
                    onClick={handleIncrement}
                    $disabled={quantity >= maxQty || isLoading}
                    disabled={quantity >= maxQty || isLoading}
                  >
                    +
                  </CounterBtn>
                </CounterRow>
              </ProductLeft>
              <PriceBlock>
                <BzcPrice>{formatPrice(totalPrice)}</BzcPrice>
                {!!totalPublicPrice && totalPublicPrice > totalPrice && (
                  <BzcPublicPrice>
                    {formatPrice(totalPublicPrice)}
                  </BzcPublicPrice>
                )}
                {!!totalEcoTax && totalEcoTax !== 0 && (
                  <EcoTax onClick={() => setShowEcoModal(true)}>
                    {t('ecoPartDont')} {totalEcoTax} €{' '}
                    <span>{t('ecoPartLabel')}</span>
                  </EcoTax>
                )}
              </PriceBlock>
            </TitleSizeBlock>
          </ProductInfo>
          <ActionContainer>
            {canMoveToWishlist && (
              <ActionWording
                onClick={handleMoveToWishlist}
                disabled={isLoading}
              >
                {t('moveToWishlist')}
              </ActionWording>
            )}
            <ActionWording onClick={handleDelete} disabled={isLoading}>
              {t('deleteProduct')}
            </ActionWording>
          </ActionContainer>
        </Container>
      )}
      <Separator />
      <BasketBarEcoResponsibleModal
        isOpen={showEcoModal}
        onClose={() => setShowEcoModal(false)}
      />
    </>
  );
};
